// FILE: src/components/admin/format.js
// Display helpers shared by the admin pages (plan WP-8). They format what
// the server sends; they never invent figures. Every helper copes with
// missing values (returns "—") so a partial payload never crashes a page.
import { auditReasonLabel, humanize, licenceClassLabel, roleLabel, ticketCategoryLabel, ticketStatusLabel } from "../../i18n/labels";
import { permissionLabel } from "../../auth/permissions";

export const DASH = "—";
const HOUR_MS = 60 * 60 * 1000;
/** MTO 10.2: every support request is answered within 48 hours. */
export const SLA_HOURS = 48;

function toDate(value) {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "7 Oct 2026" (en-CA), or "—". */
export function formatDate(value) {
  const d = toDate(value);
  return d ? d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : DASH;
}

/** "7 Oct 2026, 3:45 p.m.", or "—". */
export function formatDateTime(value) {
  const d = toDate(value);
  return d
    ? d.toLocaleString("en-CA", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : DASH;
}

/** Hours between a date and now (null when there is no date). */
export function ageHours(value, now = Date.now()) {
  const d = toDate(value);
  return d ? Math.max(0, (now - d.getTime()) / HOUR_MS) : null;
}

/** "just now", "12 min ago", "3 h ago", "2 days ago". */
export function timeAgo(value, now = Date.now()) {
  const d = toDate(value);
  if (!d) return DASH;
  const mins = Math.max(0, Math.floor((now - d.getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

/** An OPEN request older than the 48-hour answer target (MTO 10.2). */
export function isOverdueTicket(ticket, now = Date.now()) {
  if (!ticket || ticket.status !== "OPEN") return false;
  const h = ageHours(ticket.createdAt, now);
  return h !== null && h > SLA_HOURS;
}

/** "Riley Ready", falling back to the email, then to `fallback`. */
export function personName(person, fallback = "Unknown student") {
  if (!person || typeof person !== "object") return fallback;
  const name = [person.firstName, person.middleName, person.lastName]
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean)
    .join(" ");
  return name || person.email || fallback;
}

/** A number with fixed decimals, or "—" when it isn't a number (B-14). */
export function formatNumber(value, decimals = 0) {
  const n = Number(value);
  if (value === null || value === undefined || value === "" || !Number.isFinite(n)) return DASH;
  return n.toLocaleString("en-CA", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

/** 1.5 → "1.5 h"; null → "—". */
export function formatHours(value, decimals = 1) {
  const s = formatNumber(value, decimals);
  return s === DASH ? DASH : `${s} h`;
}

// ---------------------------------------------------------------------------
// CSV (compliance export, B-7). Values are quoted when needed and guarded
// against spreadsheet formula injection.
// ---------------------------------------------------------------------------
function csvCell(value) {
  if (value === null || value === undefined) return "";
  let s = String(value);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(rows) {
  return rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
}

export function downloadCsv(filename, rows) {
  const blob = new Blob([`﻿${toCsv(rows)}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ---------------------------------------------------------------------------
// Audit details → readable "label: value" pairs (never JSON on screen).
// ---------------------------------------------------------------------------
const DETAIL_LABELS = {
  ip: "IP address",
  userAgent: "Browser",
  sessionId: "Session",
  adminId: "Changed by (admin ID)",
  ticketId: "Request ID",
  resourceId: "Lesson ID",
  chapterId: "Chapter ID",
  homelinkId: "Homelink ID",
  reason: "Reason",
  factor: "Failed step",
  attempt: "Attempt",
  category: "Category",
  status: "Status",
  message: "Message",
  days: "Days",
  previousExpiresAt: "Previous end date",
  enrolmentExpiresAt: "New end date",
  attemptsRemaining: "Attempts left",
  failedAttempts: "Failed attempts",
  wasLocked: "Was locked",
  score: "Score",
  finalScore: "Final score",
  changed: "Changed",
  email: "Email",
  firstName: "First name",
  middleName: "Middle name",
  lastName: "Last name",
  phone: "Phone",
  role: "Role",
  licenseClass: "Licence class",
  licenseNumber: "Licence number",
  creditedSec: "Time credited",
  dayKey: "Day",
  // WP-7 rows: lock/unlock, enrolment and exports.
  previousLockReason: "Earlier lock reason",
  wasEnrolled: "Was enrolled",
  by: "By",
  byRole: "Asked by",
  audience: "Copy for",
  format: "File type",
  sha256: "SHA-256 fingerprint",
  from: "From",
  to: "To",
  students: "Students",
  dailyRows: "Daily rows",
  rows: "Rows",
  userId: "Account ID",
  outcome: "Outcome",
  // Staff roles (RBAC).
  roleId: "Role ID",
  name: "Name",
  description: "Description",
  permission: "Permission",
  permissions: "Permissions",
  anyOf: "Any of",
  added: "Permissions added",
  removed: "Permissions removed",
  staffRole: "Role",
  accountType: "Account type",
  ddiRegistration: "DDI registration number given",
  method: "Request",
  path: "Address"
};

const PERMISSION_KEYS = new Set(["permission", "permissions", "anyOf", "added", "removed"]);

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

export function detailLabel(key) {
  return DETAIL_LABELS[key] || humanize(key.replace(/([a-z])([A-Z])/g, "$1 $2"));
}

function detailValue(key, value) {
  if (value === null || value === undefined || value === "") return DASH;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  // A staff role ({ id, name, key, system }) is its name; permissions are their words.
  if (value && typeof value === "object" && !Array.isArray(value) && typeof value.name === "string" && ("system" in value || "key" in value)) {
    return value.name;
  }
  if (PERMISSION_KEYS.has(key) && typeof value === "string") return permissionLabel(value);
  if (key === "accountType" && typeof value === "string") return roleLabel(value);
  if (key === "reason" && value === "GRANTS_MORE_THAN_YOURS") return "Gives more than the person's own role";
  if ((key === "reason" || key === "factor") && typeof value === "string" && /^[A-Z0-9_]+$/.test(value)) {
    return auditReasonLabel(value);
  }
  if (key === "category") return ticketCategoryLabel(value);
  if (key === "licenseClass" && typeof value === "string") return licenceClassLabel(value); // NONE → "No licence yet"
  if (key === "status") return ticketStatusLabel(value);
  if (typeof value === "string" && ISO_RE.test(value)) return formatDateTime(value);
  if (Array.isArray(value)) return value.map((v) => detailValue(key, v)).join(", ") || DASH;
  if (typeof value === "object") {
    if ("from" in value || "to" in value) {
      return `${detailValue(key, value.from)} → ${detailValue(key, value.to)}`;
    }
    return Object.entries(value)
      .map(([k, v]) => `${detailLabel(k)}: ${detailValue(k, v)}`)
      .join("; ");
  }
  // Any other stored enum (e.g. CHAPTER, MAX_OTP_ATTEMPTS) in words.
  if (typeof value === "string" && /^[A-Z][A-Z_]{2,}$/.test(value)) return humanize(value);
  return String(value);
}

/** [{ label, value }] for an audit entry: its details plus who/where. */
export function describeAuditDetails(log) {
  const details = log && typeof log.details === "object" && log.details ? log.details : {};
  const out = [];
  Object.entries(details).forEach(([key, value]) => {
    if (key === "changed" && value && typeof value === "object" && !Array.isArray(value)) {
      Object.entries(value).forEach(([field, change]) => {
        out.push({ label: `${detailLabel(field)} changed`, value: detailValue(field, change) });
      });
      return;
    }
    out.push({ label: detailLabel(key), value: detailValue(key, value) });
  });
  if (log?.ip && !("ip" in details)) out.push({ label: DETAIL_LABELS.ip, value: log.ip });
  return out;
}
